import React, { useEffect } from "react";
import { observer } from "mobx-react";
import styles from "./styles/emissionViewContainer.module.css";
import { EmissionActivityViewHeader } from "./components/EmissionActivityViewHeader.js";
import { EmissionActivityCategoryTab } from "./components/EmissionActivityCategoryTab.js";
import { EmissionActivity } from "./sections/EmissionActivity";
import { DataEntry } from "./sections/DataEntry.js";
import { Entity } from "./sections/Entity.js";
import { EmissionFactor } from "./sections/EmissionFactor";
import { Emission } from "./sections/Emission";
import {
  EmissionRemovalMitigation,
  RMActivityModal,
} from "./sections/EmissionRemovalMitigation";
import { EmissionYearSelector } from "./sections/EmissionYearSelector.js";
import { EAEFModal } from "./modals/EmissionActivity/EAEFModal.jsx";
import { EFModal } from "./modals/EmissionFactor/EFModal.jsx";
import { EmissionFactorIncertModal } from "./modals/EmissionFactor/EmissionFactorIncertModal.js";
import DataEntryUnitMismatchModal from "./modals/DataEntry/DataEntryUnitMismatchModal.js";
import emissionViewStore from "../Store/marco/emissionViewStore.js";
import { getConstByStandard } from "./utils/getConstFileByStandard.js";
import { useTranslation } from "react-i18next";
import commonStore from "../Store/marco/commonStore.js";
import {
  getEditableSubTreeIds,
  getFilterCom,
} from "./utils/getFilterCom.js";
import { AddCustomEFModal } from "./modals/EmissionActivity/AddCustomEFModal.js";
import { Pagination } from "../Tools/Pagination.jsx";
import { ExcelImportExportModal } from "../ExcelImportExportModal/ExcelImportExportModal.js";
import { DataEntryExcelModal } from "./modals/DataEntry/DataEntryExcelModal.js";
import { LoadingScreen } from "./components/LoadingScreen.jsx";
import { isWorkflowImportType } from "../import-job/import-job-config.js";
import {
  EMISSION_REMOVAL_MITIGATION_ENABLED,
  EMISSION_REMOVAL_MITIGATION_LABEL,
  getEnabledEmissionCategoryOptions,
  isEmissionRemovalMitigation,
} from "./utils/emissionRemovalMitigation.js";

import { InventoryCompanyGuard } from "./components/inventory-company-guard";

export const EmissionViewContainer = observer(() => {
  const { t } = useTranslation("ActivityMain");
  const cmStore = commonStore;
  const store = emissionViewStore;
  const CONST = getConstByStandard(store.standardId);
  const isRMWorkflow =
    EMISSION_REMOVAL_MITIGATION_ENABLED &&
    isEmissionRemovalMitigation(store.selectedScope);

  const viewComponentMap = {
    "Emission Activity": <EmissionActivity />,
    "Data Entry": <DataEntry />,
    "Emission Factor": <EmissionFactor />,
    Emission: <Emission />,
    Entity: <Entity />,
    Year: <EmissionYearSelector />,
  };

  const handlePageChange = (newPage) => {
    store._handleSetSharedListPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    store._handleSetSharedListPageSize(newPageSize);
  };

  const paginationListDataMap = {
    "Emission Activity": store.emissionActivityListData,
    "Data Entry": store.dataEntryListData,
    "Emission Factor": store.emissionFactorListData,
    Emission: store.emissionListData,
  };

  const currentPagination = paginationListDataMap[store.selectedView];
  const rmPagination =
    isRMWorkflow && store.selectedView === "Emission Activity"
      ? store.rmActivityListData
      : null;

  const paginationEnabledViews = [
    "Emission Activity",
    "Data Entry",
    "Emission Factor",
    "Emission",
  ];

  const shouldShowPagination =
    rmPagination
      ? rmPagination.fullList?.length > 0
      : paginationEnabledViews.includes(store.selectedView) &&
        currentPagination?.fullList?.length > 0;

  //初始化
  useEffect(() => {
    if (cmStore.roleId == 2) {
      emissionViewStore._handleSetCurrentCompanyName("Super Admin");
    } else {
      emissionViewStore._handleSetCurrentCompanyName(cmStore.companyName || "");
    }
    //// 設定當前公司 ID（可能是多個）
    const currentScopeNodeId =
      cmStore.scopeType === "COMPANY"
        ? cmStore.companyTreeRootId
        : cmStore.scopeId;
    store._handleSetCurrentCId(currentScopeNodeId);

    ////////取出對應類別下的公司
    const filterCom =
      cmStore.scopeType === "COMPANY"
        ? getEditableSubTreeIds(
            cmStore.companyTreeRootId,
            cmStore.companyList,
          )
        : getFilterCom(cmStore.orgList, cmStore.authId, cmStore.rootId);

    ////類別底下的公司列表
    store._handleSetAuthComArr(filterCom);
  }, []);

  useEffect(() => {
    if (
      EMISSION_REMOVAL_MITIGATION_ENABLED ||
      !isEmissionRemovalMitigation(store.selectedScope)
    ) {
      return;
    }

    const fallbackScope = getEnabledEmissionCategoryOptions(
      CONST.categoryDropdown,
    )[0];
    if (fallbackScope) {
      store._handleSetSelectedScope(
        fallbackScope.value ?? fallbackScope.label,
      );
    }
  }, [store.selectedScope, store.standardId]);

  useEffect(() => {
    if (!store.selectedYear) return;
    cmStore.setEACategory(store.selectedYear, store.standardId);
    store._handleSetSelectedTab(
      isRMWorkflow
        ? EMISSION_REMOVAL_MITIGATION_LABEL
        : CONST.tableTitles[0].label,
    );
  }, [store.selectedYear]);

  useEffect(() => {
    return () => {
      store._handleSetAuthComArr([]);
      store._handleSetSelectedView("Year");
      store._handleSetSelectedYear(null);
    };
  }, []);

  // 根據 selectedView 決定要使用的 loading 狀態
  const getLoadingState = () => {
    switch (store.selectedView) {
      case "Emission Activity":
        return store.emissionActivityListLoading;
      case "Data Entry":
        return store.dataEntryListLoading;
      case "Emission Factor":
        return store.emissionActivityListLoading;
      case "Emission":
        return store.emissionListLoading;
      case "Entity":
        if (cmStore.scopeType === "COMPANY") {
          return !cmStore.companyList || cmStore.companyList.length === 0;
        }
        // 如果是 AUTH，才需要等 orgList 載入
        return !cmStore.orgList || cmStore.orgList.length === 0;
      case "Year":
        return false; // EmissionYearSelector 有自己的 local loading
      default:
        return false;
    }
  };

  return (
    <InventoryCompanyGuard>
    <div className={styles.container}>
      <div className={styles.title}>
        <h4 className={styles.titleH4}>
          {t(store.currentCompanyName)} ({t("Year")}:{store.selectedYear})
        </h4>
      </div>
      <div className={styles.main}>
        {/**渲染切換頁面的五個按鈕，範疇選項以及Search*/}
        <EmissionActivityViewHeader />
        {/**渲染card tab*/}
        {store.selectedView !== "Year" && store.selectedView !== "Entity" ? (
          <EmissionActivityCategoryTab />
        ) : null}
        {/**依條件渲染不同的components*/}

        {isRMWorkflow &&
        ["Emission Activity", "Emission Reduction", "Summary"].includes(
          store.selectedView,
        ) ? (
          <EmissionRemovalMitigation />
        ) : (
          viewComponentMap[store.selectedView] ?? <EmissionYearSelector />
        )}

        {/**統一的 loading 畫面 */}
        <LoadingScreen isLoading={getLoadingState()} />

        {shouldShowPagination && (
          <Pagination
            page={rmPagination ? rmPagination.page : currentPagination.page}
            pageSize={
              rmPagination ? rmPagination.pageSize : currentPagination.pageSize
            }
            total={
              rmPagination
                ? rmPagination.fullList.length
                : currentPagination.fullList.length
            }
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            className={styles.pagination}
          />
        )}
      </div>

      {/**顯示排放係數新增自定義表單 */}
      {store.showAddCustomEFModal === true ? <AddCustomEFModal /> : null}

      {/* 單位不對襯提醒modal */}
      <DataEntryUnitMismatchModal />

      {/* excelModal */}
      {cmStore.showExcelImportExportModal &&
      !isWorkflowImportType(cmStore.excelImportType) ? (
        <ExcelImportExportModal />
      ) : null}

      {/* {store.isShowDataEntryExcelModal ? <DataEntryExcelModal /> : null} */}
      {store.showEmissionActivityModal ? (
        isRMWorkflow ? (
          <RMActivityModal />
        ) : (
          <EAEFModal />
        )
      ) : null}
      {store.showEmissionFactorEditModal ? <EFModal /> : null}
      {store.isShowEFIncertModal ? <EmissionFactorIncertModal /> : null}

      {/**分頁元件 */}
    </div>
    </InventoryCompanyGuard>
  );
});
